import { jwtDecode } from "jwt-decode";

/**
 * Extracts user information from an Auth0 JWT ID token
 * @param {string} idToken - The JWT ID token from Auth0
 * @returns {Object} User information object with username, email, and avatar
 */
export const extractUserInfoFromToken = (idToken) => {
	if (!idToken) {
		return null;
	}

	try {
		const decodedToken = jwtDecode(idToken);
		return {
			username: decodedToken.nickname || null,
			email: decodedToken.name || decodedToken.email || null,
			avatar: decodedToken.picture || null,
			signupSource: decodedToken["https://portal.metabolon.com/user_metadata"]?.signupSource || null
		};
	} catch (error) {
		console.warn('Failed to decode ID token:', error);
		return null;
	}
};

/**
 * Stores user info from JWT token in localStorage (for backwards compatibility)
 * @param {string} idToken - The JWT ID token from Auth0
 */
export const storeUserInfoFromToken = (idToken) => {
	const userInfo = extractUserInfoFromToken(idToken);
	if (!userInfo) {
		return;
	}

	if (userInfo.username) {
		localStorage.setItem('username', userInfo.username);
	}
	if (userInfo.email) {
		localStorage.setItem('email', userInfo.email);
	}
	if (userInfo.avatar) {
		localStorage.setItem('avatar', userInfo.avatar);
	}
	if (userInfo.signupSource) {
		localStorage.setItem('signup_source', userInfo.signupSource);
	}
};

/**
 * Checks if a token is valid (not expired)
 * @param {string} token - JWT token to validate
 * @returns {boolean} True if token exists and hasn't expired, false otherwise
 */
export const hasValidToken = (token) => {
	if (!token) return false;
	try {
		// Decode token to check expiration
		const decoded = extractUserInfoFromToken(token);
		if (!decoded || !decoded.exp) return false;
		// Check if token is expired (exp is in seconds, Date.now() is in milliseconds)
		return decoded.exp * 1000 > new Date().getTime();
	} catch {
		return false;
	}
};

